import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { collection, addDoc, doc, setDoc, getDocs, query, where, deleteDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { SharedOrderFormData } from '../components/forms/shared/SharedFormSections';
import { PDFService } from '../services/PDFService';
import { EmailService } from '../services/EmailService';
import { useFormCache } from './useFormCache';
import { FormCacheData, FormStatus, ProgressInfo } from '../services/FormCacheService';
import { getLivingstonProductName, getLivingstonProductCode, getLivingstonProductPrice, getLivingstonProductCategory, isGenericProductName, mapGenericProductName } from '../utils/productMappings';

// Order item interface
export interface OrderItem {
  id: string;
  packetDescription: string;
  code: string;
  quantity: number;
  price: number;
  category: string;
}

// Extended order data interface
export interface GlobalOrderData extends SharedOrderFormData {
  // Heritage Order items
  heritageItems?: OrderItem[];
  
  // Livingston Order items  
  livingstonItems?: OrderItem[];
  
  // Flower Order items
  flowerItems?: OrderItem[];
  
  // Vegetable Order items
  vegetableItems?: OrderItem[];
  
  // Supply Order items
  supplyItems?: OrderItem[];
  
  // Metadata
  orderId?: string;
  createdAt?: Date;
  updatedAt?: Date;
  submittedAt?: Date;
  status?: 'draft' | 'submitted' | 'processing' | 'completed';
  
  // Draft-specific fields
  isDraft?: boolean;
  draftSavedAt?: Date;
  
  // Finalized order-specific fields
  finalizedAt?: Date;
  finalizedBy?: string; // User ID who finalized the order
  originalDraftId?: string; // Reference to the draft it came from
  
  // Customer signature (base64 encoded image)
  customerSignature?: string;
}

interface GlobalOrderContextType {
  // Data
  orderData: GlobalOrderData;
  isLoading: boolean;
  error: string | null;
  
  // Cache management
  cacheData: FormCacheData | null;
  isCacheLoading: boolean;
  isCacheSaving: boolean;
  lastSaved: Date | null;
  hasUnsavedChanges: boolean;
  
  // Actions
  updateOrderData: (data: Partial<GlobalOrderData>) => void;
  saveOrderData: () => Promise<void>;
  submitOrder: () => Promise<{ success: boolean; orderId?: string; pdfUrl?: string }>;
  submitOrderWithEmail: (customerEmail: string) => Promise<{ success: boolean; orderId?: string; pdfUrl?: string }>;
  resetOrderData: () => Promise<void>;
  
  // Cache actions
  saveDraft: () => Promise<void>;
  loadDraft: () => Promise<void>;
  clearDraft: () => Promise<void>;
  autoSave: () => void;
  
  // Form-specific actions
  updateHeritageItems: (items: OrderItem[]) => void;
  updateLivingstonItems: (items: OrderItem[]) => void;
  updateFlowerItems: (items: OrderItem[]) => void;
  updateVegetableItems: (items: OrderItem[]) => void;
  
  // Status indicators
  getFormStatus: (formType: string) => FormStatus;
  getTotalProgress: () => ProgressInfo;
}

const GlobalOrderContext = createContext<GlobalOrderContextType | undefined>(undefined);

// PDF Generation Helper
const generateOrderPDF = async (orderData: GlobalOrderData): Promise<Blob> => {
  return await PDFService.generateOrderPDF({
    orderData,
    orderId: orderData.orderId || `order_${Date.now()}`,
    customerEmail: orderData.email
  });
};

// Email Sending Helper
const sendOrderEmails = async (
  customerEmail: string, 
  pdfBlob: Blob, 
  orderId: string, 
  orderData: GlobalOrderData
): Promise<void> => {
  // Initialize email service if not already done
  EmailService.initialize();
  await EmailService.sendOrderConfirmation(customerEmail, pdfBlob, orderId, orderData);
};

// Initial order data
const getInitialOrderData = (): GlobalOrderData => ({
  // Customer Information (will be populated from Firebase)
  customerNumber: '',
  storeName: '',
  address: '',
  city: '',
  province: '',
  postalCode: '',
  email: '',
  phoneNumber: '',
  
  // Order Information
  poNumber: '',
  orderDate: new Date().toISOString().split('T')[0],
  deliveryDate: '',
  orderType: null,
  specialInstructions: '',
  
  // Order items
  heritageItems: [],
  livingstonItems: [],
  flowerItems: [],
  vegetableItems: [],
  
  // Metadata
  createdAt: new Date(),
  updatedAt: new Date(),
  status: 'draft'
});

export const GlobalOrderProvider: React.FC<{ 
  children: React.ReactNode; 
  storeId: string;
}> = ({ children, storeId }) => {
  const [orderData, setOrderData] = useState<GlobalOrderData>(getInitialOrderData());
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isCacheInitialized, setIsCacheInitialized] = useState(false);
  const [isDataLoadedFromStore, setIsDataLoadedFromStore] = useState(false);
  
  // Ref to track latest orderData without causing dependency issues
  const orderDataRef = useRef<GlobalOrderData>(orderData);
  
  // Update ref whenever orderData changes
  useEffect(() => {
    orderDataRef.current = orderData;
  }, [orderData]);

  // Initialize cache with storeId as userId
  const {
    cacheData,
    isLoading: isCacheLoading,
    isSaving: isCacheSaving,
    lastSaved,
    hasUnsavedChanges,
    saveCache,
    loadCache,
    clearCache,
    autoSave: cacheAutoSave,
    getFormStatus,
    getTotalProgress,
  } = useFormCache({
    userId: storeId,
    autoSaveDelay: 2000,
    enableFirebase: true,
  });

  // Load existing order data when storeId changes
  useEffect(() => {
    if (storeId) {
      loadExistingOrderData();
    }
  }, [storeId]);

  // Sync cache data with order data (only on initial load to prevent overwriting user changes)
  // Only merge cache if StoreOrders data wasn't loaded (cache is fallback)
  // IMPORTANT: This effect must run AFTER loadExistingOrderData completes
  useEffect(() => {
    // Only run if:
    // 1. Cache data exists
    // 2. Cache hasn't been initialized yet
    // 3. Data was NOT loaded from StoreOrders (cache is fallback only)
    // 4. Loading is complete (to avoid race conditions)
    if (cacheData && !isCacheInitialized && !isDataLoadedFromStore && !isLoading) {
      // Convert cache data to order data format
      const cachedOrderData: Partial<GlobalOrderData> = {
        ...cacheData.orderData,
        // Cast orderType to the correct union type
        orderType: (cacheData.orderData.orderType === 'custOrder' || cacheData.orderData.orderType === 'offCarDelivery') 
          ? cacheData.orderData.orderType as 'custOrder' | 'offCarDelivery' 
          : null,
        heritageItems: Object.entries(cacheData.formSelections.heritage)
          .filter(([_, item]) => {
            const qty = typeof item === 'number' ? item : item.quantity;
            return qty > 0;
          })
          .map(([id, item]) => {
            // Support both old format (number) and new format (object with full details)
            if (typeof item === 'number') {
              // Legacy format - generate placeholder
              return {
                id,
                packetDescription: `Heritage Product ${id}`,
                code: `H-${id}`,
                quantity: item,
                price: 3.79,
                category: 'heritage'
              };
            }
            // New format - use stored product details
            return {
              id,
              packetDescription: item.packetDescription || '',
              code: item.code || '',
              quantity: item.quantity,
              price: item.price || 0,
              category: item.category || 'heritage'
            };
          }),
        livingstonItems: Object.entries(cacheData.formSelections.livingston)
          .filter(([_, item]) => {
            const qty = typeof item === 'number' ? item : item.quantity;
            return qty > 0;
          })
          .map(([id, item]) => {
            if (typeof item === 'number') {
              // Map product ID to actual product name from Livingston products
              const productName = getLivingstonProductName(id);
              const productCode = getLivingstonProductCode(id);
              const productPrice = getLivingstonProductPrice(id);
              const productCategory = getLivingstonProductCategory(id);
              return {
                id,
                packetDescription: productName,
                code: productCode,
                quantity: item,
                price: productPrice,
                category: productCategory
              };
            }
            return {
              id,
              packetDescription: item.packetDescription || '',
              code: item.code || '',
              quantity: item.quantity,
              price: item.price || 0,
              category: item.category || 'livingston'
            };
          }),
        flowerItems: Object.entries(cacheData.formSelections.flower)
          .filter(([_, item]) => {
            const qty = typeof item === 'number' ? item : item.quantity;
            return qty > 0;
          })
          .map(([id, item]) => {
            if (typeof item === 'number') {
              return {
                id,
                packetDescription: `Flower Product ${id}`,
                code: `F-${id}`,
                quantity: item,
                price: 2.49,
                category: 'flower'
              };
            }
            return {
              id,
              packetDescription: item.packetDescription || '',
              code: item.code || '',
              quantity: item.quantity,
              price: item.price || 0,
              category: item.category || 'flower'
            };
          }),
        vegetableItems: Object.entries(cacheData.formSelections.vegetable)
          .filter(([_, item]) => {
            const qty = typeof item === 'number' ? item : item.quantity;
            return qty > 0;
          })
          .map(([id, item]) => {
            if (typeof item === 'number') {
              return {
                id,
                packetDescription: `Vegetable Product ${id}`,
                code: `V-${id}`,
                quantity: item,
                price: 2.49,
                category: 'vegetable'
              };
            }
            return {
              id,
              packetDescription: item.packetDescription || '',
              code: item.code || '',
              quantity: item.quantity,
              price: item.price || 0,
              category: item.category || 'vegetable'
            };
          }),
      };

      setOrderData(prev => {
        // Only merge cached data if it's actually populated
        const mergedOrderData = { ...prev };
        
        // Only overwrite customer data if cache has actual values
        if (cachedOrderData.customerNumber) mergedOrderData.customerNumber = cachedOrderData.customerNumber;
        if (cachedOrderData.storeName) mergedOrderData.storeName = cachedOrderData.storeName;
        if (cachedOrderData.address) mergedOrderData.address = cachedOrderData.address;
        if (cachedOrderData.city) mergedOrderData.city = cachedOrderData.city;
        if (cachedOrderData.province) mergedOrderData.province = cachedOrderData.province;
        if (cachedOrderData.postalCode) mergedOrderData.postalCode = cachedOrderData.postalCode;
        if (cachedOrderData.email) mergedOrderData.email = cachedOrderData.email;
        if (cachedOrderData.phoneNumber) mergedOrderData.phoneNumber = cachedOrderData.phoneNumber;
        
        // Order information
        if (cachedOrderData.poNumber !== undefined) mergedOrderData.poNumber = cachedOrderData.poNumber;
        if (cachedOrderData.orderDate) mergedOrderData.orderDate = cachedOrderData.orderDate;
        if (cachedOrderData.deliveryDate) mergedOrderData.deliveryDate = cachedOrderData.deliveryDate;
        if (cachedOrderData.orderType !== undefined) mergedOrderData.orderType = cachedOrderData.orderType;
        if (cachedOrderData.specialInstructions !== undefined) mergedOrderData.specialInstructions = cachedOrderData.specialInstructions;
        
        // Only merge items if they exist in cache and weren't already loaded from StoreOrders
        // This prevents cache from overwriting StoreOrders data
        if (cachedOrderData.heritageItems && cachedOrderData.heritageItems.length > 0) {
          mergedOrderData.heritageItems = cachedOrderData.heritageItems;
        }
        if (cachedOrderData.livingstonItems && cachedOrderData.livingstonItems.length > 0) {
          mergedOrderData.livingstonItems = cachedOrderData.livingstonItems;
        }
        if (cachedOrderData.flowerItems && cachedOrderData.flowerItems.length > 0) {
          mergedOrderData.flowerItems = cachedOrderData.flowerItems;
        }
        if (cachedOrderData.vegetableItems && cachedOrderData.vegetableItems.length > 0) {
          mergedOrderData.vegetableItems = cachedOrderData.vegetableItems;
        }
        mergedOrderData.updatedAt = new Date();
        
        return mergedOrderData;
      });
      setIsCacheInitialized(true);
    }
  }, [cacheData, isCacheInitialized, isDataLoadedFromStore, isLoading]);

  const loadExistingOrderData = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Query for existing draft orders for this store
      const ordersRef = collection(db, 'Stores', storeId, 'StoreOrders');
      const querySnapshot = await getDocs(ordersRef);
      
      if (!querySnapshot.empty) {
        // Per store, only ONE order should be in draft/in-progress
        // If multiple drafts exist, keep only the most recent one and delete the rest
        const drafts = querySnapshot.docs;
        
        if (drafts.length > 1) {
          console.log(`⚠️ Found ${drafts.length} drafts for store ${storeId}. Keeping only the most recent one and deleting others...`);
          
          // Sort by updatedAt or draftSavedAt to find the most recent
          const sortedDrafts = drafts.sort((a, b) => {
            const aDate = a.data().updatedAt?.toDate?.() || a.data().draftSavedAt?.toDate?.() || new Date(0);
            const bDate = b.data().updatedAt?.toDate?.() || b.data().draftSavedAt?.toDate?.() || new Date(0);
            return bDate.getTime() - aDate.getTime();
          });
          
          // Keep the most recent draft
          const latestOrder = sortedDrafts[0];
          
          // Delete all other drafts
          for (let i = 1; i < sortedDrafts.length; i++) {
            try {
              await deleteDoc(doc(db, 'Stores', storeId, 'StoreOrders', sortedDrafts[i].id));
              console.log(`🗑️ Deleted extra draft: ${sortedDrafts[i].id}`);
            } catch (deleteErr) {
              console.error(`⚠️ Failed to delete draft ${sortedDrafts[i].id}:`, deleteErr);
            }
          }
          
          // Load the most recent draft order
          const data = latestOrder.data() as GlobalOrderData;
          
          // Ensure all order information fields are properly loaded (handle Firestore timestamp conversion)
          // Preserve item arrays exactly as they are in StoreOrders - no transformation
          const loadedOrderData: GlobalOrderData = {
            ...getInitialOrderData(), // Start with defaults
            ...data, // Override with loaded data
            orderId: latestOrder.id,
            updatedAt: new Date(),
            // Preserve order information fields from StoreOrders
            poNumber: data.poNumber || '',
            orderDate: data.orderDate || getInitialOrderData().orderDate,
            deliveryDate: data.deliveryDate || '',
            orderType: data.orderType || null,
            specialInstructions: data.specialInstructions || '',
            // Explicitly preserve item arrays exactly as stored - no transformation
            heritageItems: Array.isArray(data.heritageItems) ? data.heritageItems.map(item => ({
              ...item,
              // Fix generic product names if they exist
              packetDescription: isGenericProductName(item.packetDescription) 
                ? mapGenericProductName(item.packetDescription, item.id, item.category || 'heritage')
                : item.packetDescription
            })) : [],
            livingstonItems: Array.isArray(data.livingstonItems) ? data.livingstonItems.map(item => ({
              ...item,
              // Fix generic product names if they exist
              packetDescription: isGenericProductName(item.packetDescription) 
                ? mapGenericProductName(item.packetDescription, item.id, item.category || 'livingston')
                : item.packetDescription
            })) : [],
            flowerItems: Array.isArray(data.flowerItems) ? data.flowerItems.map(item => ({
              ...item,
              // Fix generic product names if they exist
              packetDescription: isGenericProductName(item.packetDescription) 
                ? mapGenericProductName(item.packetDescription, item.id, item.category || 'flower')
                : item.packetDescription
            })) : [],
            vegetableItems: Array.isArray(data.vegetableItems) ? data.vegetableItems.map(item => ({
              ...item,
              // Fix generic product names if they exist
              packetDescription: isGenericProductName(item.packetDescription) 
                ? mapGenericProductName(item.packetDescription, item.id, item.category || 'vegetable')
                : item.packetDescription
            })) : []
          };
          
          // Set flag FIRST to prevent cache from overwriting
          setIsDataLoadedFromStore(true);
          // Then set the data
          setOrderData(loadedOrderData);
          console.log('✅ Loaded order data from StoreOrders (multiple drafts):', {
            heritageItems: loadedOrderData.heritageItems?.length || 0,
            livingstonItems: loadedOrderData.livingstonItems?.length || 0,
            flowerItems: loadedOrderData.flowerItems?.length || 0,
            vegetableItems: loadedOrderData.vegetableItems?.length || 0
          });
        } else {
          // Only one draft exists, load it
          const latestOrder = drafts[0];
          const data = latestOrder.data() as GlobalOrderData;
          
          // Ensure all order information fields are properly loaded (handle Firestore timestamp conversion)
          // Preserve item arrays exactly as they are in StoreOrders - no transformation
          const loadedOrderData: GlobalOrderData = {
            ...getInitialOrderData(), // Start with defaults
            ...data, // Override with loaded data
            orderId: latestOrder.id,
            updatedAt: new Date(),
            // Preserve order information fields from StoreOrders
            poNumber: data.poNumber || '',
            orderDate: data.orderDate || getInitialOrderData().orderDate,
            deliveryDate: data.deliveryDate || '',
            orderType: data.orderType || null,
            specialInstructions: data.specialInstructions || '',
            // Explicitly preserve item arrays exactly as stored - no transformation
            heritageItems: Array.isArray(data.heritageItems) ? data.heritageItems.map(item => ({
              ...item,
              // Fix generic product names if they exist
              packetDescription: isGenericProductName(item.packetDescription) 
                ? mapGenericProductName(item.packetDescription, item.id, item.category || 'heritage')
                : item.packetDescription
            })) : [],
            livingstonItems: Array.isArray(data.livingstonItems) ? data.livingstonItems.map(item => ({
              ...item,
              // Fix generic product names if they exist
              packetDescription: isGenericProductName(item.packetDescription) 
                ? mapGenericProductName(item.packetDescription, item.id, item.category || 'livingston')
                : item.packetDescription
            })) : [],
            flowerItems: Array.isArray(data.flowerItems) ? data.flowerItems.map(item => ({
              ...item,
              // Fix generic product names if they exist
              packetDescription: isGenericProductName(item.packetDescription) 
                ? mapGenericProductName(item.packetDescription, item.id, item.category || 'flower')
                : item.packetDescription
            })) : [],
            vegetableItems: Array.isArray(data.vegetableItems) ? data.vegetableItems.map(item => ({
              ...item,
              // Fix generic product names if they exist
              packetDescription: isGenericProductName(item.packetDescription) 
                ? mapGenericProductName(item.packetDescription, item.id, item.category || 'vegetable')
                : item.packetDescription
            })) : []
          };
          
          // Set flag FIRST to prevent cache from overwriting
          setIsDataLoadedFromStore(true);
          // Then set the data
          setOrderData(loadedOrderData);
          console.log('✅ Loaded order data from StoreOrders (single draft):', {
            heritageItems: loadedOrderData.heritageItems?.length || 0,
            livingstonItems: loadedOrderData.livingstonItems?.length || 0,
            flowerItems: loadedOrderData.flowerItems?.length || 0,
            vegetableItems: loadedOrderData.vegetableItems?.length || 0
          });
        }
      } else {
        // No draft orders exist, create new draft order data (but don't save yet)
        const newOrderData = getInitialOrderData();
        setOrderData(newOrderData);
        setIsDataLoadedFromStore(false); // No StoreOrders data, can use cache
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load order data');
      console.error('Error loading order data:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // List of order information fields that should save immediately
  const orderInfoFields: (keyof GlobalOrderData)[] = [
    'poNumber',
    'orderDate',
    'deliveryDate',
    'orderType',
    'specialInstructions'
  ];

  // Immediate save function for order information fields
  const saveOrderInfoToStoreOrders = useCallback(async (data: GlobalOrderData) => {
    try {
      // Check if there are any other draft orders (to ensure only one exists)
      const ordersRef = collection(db, 'Stores', storeId, 'StoreOrders');
      const querySnapshot = await getDocs(ordersRef);
      
      // If there are other drafts (not the current one), delete them
      if (!querySnapshot.empty) {
        const otherDrafts = querySnapshot.docs.filter(doc => doc.id !== data.orderId);
        
        if (otherDrafts.length > 0) {
          console.log(`⚠️ Found ${otherDrafts.length} other draft(s) for store ${storeId}. Deleting them...`);
          
          // Delete all other drafts
          for (const draftDoc of otherDrafts) {
            try {
              await deleteDoc(doc(db, 'Stores', storeId, 'StoreOrders', draftDoc.id));
              console.log(`🗑️ Deleted extra draft: ${draftDoc.id}`);
            } catch (deleteErr) {
              console.error(`⚠️ Failed to delete draft ${draftDoc.id}:`, deleteErr);
            }
          }
        }
      }
      
      // Ensure all order information fields are included in the save
      const dataToSave = {
        ...data,
        // Explicitly include all order information fields
        poNumber: data.poNumber || '',
        orderDate: data.orderDate || '',
        deliveryDate: data.deliveryDate || '',
        orderType: data.orderType || null,
        specialInstructions: data.specialInstructions || '',
        updatedAt: new Date(),
        status: 'draft',
        isDraft: true,
        draftSavedAt: new Date()
      };
      
      if (data.orderId) {
        // Update existing order
        const orderRef = doc(db, 'Stores', storeId, 'StoreOrders', data.orderId);
        await setDoc(orderRef, dataToSave, { merge: true });
        console.log('✅ Order information saved to StoreOrders immediately');
      } else {
        // Create new order
        const ordersRef = collection(db, 'Stores', storeId, 'StoreOrders');
        const docRef = await addDoc(ordersRef, dataToSave);
        // Update the orderId in state
        setOrderData(prev => ({
          ...prev,
          orderId: docRef.id
        }));
        console.log('✅ New order created in StoreOrders');
      }
    } catch (err: any) {
      console.error('⚠️ Failed to save order information to StoreOrders:', err);
      // Don't throw - this is a background save
    }
  }, [storeId]);

  // Ref to track if we're currently saving to prevent infinite loops
  const isSavingRef = useRef(false);
  
  // Internal function to save to StoreOrders without blocking UI
  // Ensures only ONE draft exists per store
  // This saves ALL order data including product selections (heritageItems, livingstonItems, flowerItems, vegetableItems)
  // MUST be defined before updateOrderData to avoid initialization error
  // Track if a save is pending to ensure we always save the latest data
  const pendingSaveRef = useRef<boolean>(false);
  
  const saveToStoreOrders = useCallback(async (dataToSaveOverride?: GlobalOrderData) => {
    // If a save is already in progress, mark that we need to save again after this one completes
    if (isSavingRef.current) {
      pendingSaveRef.current = true;
      console.log('⏸️ Save already in progress, will save again after current save completes...');
      return;
    }
    
    try {
      isSavingRef.current = true;
      
      // Always use the latest data from the ref to ensure we never save stale data
      // This is critical when multiple product selections happen quickly
      const dataToUse = dataToSaveOverride || orderDataRef.current;
      
      if (!dataToUse) {
        console.warn('⚠️ No data to save to StoreOrders');
        return;
      }
      
      // First, check if there are any other draft orders (to ensure only one exists)
      const ordersRef = collection(db, 'Stores', storeId, 'StoreOrders');
      const querySnapshot = await getDocs(ordersRef);
      
      // If there are other drafts (not the current one), delete them
      if (!querySnapshot.empty) {
        const otherDrafts = querySnapshot.docs.filter(doc => doc.id !== dataToUse!.orderId);
        
        if (otherDrafts.length > 0) {
          console.log(`⚠️ Found ${otherDrafts.length} other draft(s) for store ${storeId}. Deleting them to ensure only one draft exists...`);
          
          // Delete all other drafts
          for (const draftDoc of otherDrafts) {
            try {
              await deleteDoc(doc(db, 'Stores', storeId, 'StoreOrders', draftDoc.id));
              console.log(`🗑️ Deleted extra draft: ${draftDoc.id}`);
            } catch (deleteErr) {
              console.error(`⚠️ Failed to delete draft ${draftDoc.id}:`, deleteErr);
            }
          }
        }
      }
      
      // Save ALL order data including product selections
      const dataToSave = {
        ...dataToUse,
        // Explicitly include all product items to ensure they're saved
        heritageItems: dataToUse.heritageItems || [],
        livingstonItems: dataToUse.livingstonItems || [],
        flowerItems: dataToUse.flowerItems || [],
        vegetableItems: dataToUse.vegetableItems || [],
        supplyItems: dataToUse.supplyItems || [],
        updatedAt: new Date(),
        status: 'draft',
        isDraft: true,
        draftSavedAt: new Date()
      };
      
      if (dataToUse.orderId) {
        // Update existing order
        const orderRef = doc(db, 'Stores', storeId, 'StoreOrders', dataToUse.orderId);
        await setDoc(orderRef, dataToSave, { merge: true });
        console.log('✅ Draft order updated in StoreOrders with product selections');
      } else {
        // Create new order
        const ordersRef = collection(db, 'Stores', storeId, 'StoreOrders');
        const docRef = await addDoc(ordersRef, dataToSave);
        // Update orderId without triggering updateOrderData or save loops
        // Use functional update and only update if orderId actually changed
        setOrderData(prev => {
          if (prev.orderId === docRef.id) {
            return prev; // No change needed
          }
          // Update orderId and ref, but don't trigger another save
          const updated = { ...prev, orderId: docRef.id };
          orderDataRef.current = updated;
          return updated;
        });
        console.log('✅ New draft order created in StoreOrders with product selections');
      }
      
      console.log('✅ Draft order saved to StoreOrders successfully', {
        orderId: dataToSave.orderId,
        heritageItems: dataToSave.heritageItems?.length || 0,
        livingstonItems: dataToSave.livingstonItems?.length || 0,
        flowerItems: dataToSave.flowerItems?.length || 0,
        vegetableItems: dataToSave.vegetableItems?.length || 0
      });
    } catch (err: any) {
      console.error('⚠️ Failed to save draft to StoreOrders:', err);
      // Don't throw - this is a background save
    } finally {
      isSavingRef.current = false;
      
      // If there's a pending save, execute it now with the latest data from the ref
      // This ensures we always save the most up-to-date data even when multiple saves are queued
      if (pendingSaveRef.current) {
        pendingSaveRef.current = false;
        setTimeout(() => {
          // Call saveToStoreOrders without override to use the latest data from orderDataRef
          saveToStoreOrders();
        }, 50);
      }
    }
  }, [storeId]); // Removed orderData from dependencies to prevent infinite loops

  const updateOrderData = useCallback((data: Partial<GlobalOrderData>) => {
    setOrderData(prev => {
      const updatedData = {
        ...prev,
        ...data,
        updatedAt: new Date()
      };
      
      // Check if any order information fields are being updated
      const hasOrderInfoFields = Object.keys(data).some(key => 
        orderInfoFields.includes(key as keyof GlobalOrderData)
      );
      
      // Check if product items are being updated (heritageItems, livingstonItems, flowerItems, vegetableItems, supplyItems)
      const hasProductItems = Object.keys(data).some(key => 
        ['heritageItems', 'livingstonItems', 'flowerItems', 'vegetableItems', 'supplyItems'].includes(key)
      );
      
      // If order information fields are updated, save immediately to Firestore
      // Also trigger cache save to ensure order information persists on refresh
      if (hasOrderInfoFields) {
        // Use a timeout to ensure state has updated, then save
        setTimeout(() => {
          saveOrderInfoToStoreOrders(updatedData);
          // Trigger cache save to ensure order information is cached
          // This ensures data persists even if Firestore is slow or unavailable
          // Use updatedData which already contains all merged data
          cacheAutoSave({
            orderData: {
              customerNumber: updatedData.customerNumber,
              storeName: updatedData.storeName,
              address: updatedData.address,
              city: updatedData.city,
              province: updatedData.province,
              postalCode: updatedData.postalCode,
              email: updatedData.email,
              phoneNumber: updatedData.phoneNumber,
              poNumber: updatedData.poNumber,
              orderDate: updatedData.orderDate,
              deliveryDate: updatedData.deliveryDate,
              orderType: updatedData.orderType,
              specialInstructions: updatedData.specialInstructions,
            },
            formSelections: {
              heritage: updatedData.heritageItems?.reduce((acc, item) => {
                acc[item.id] = {
                  quantity: item.quantity,
                  packetDescription: item.packetDescription,
                  code: item.code,
                  price: item.price,
                  category: item.category
                };
                return acc;
              }, {} as { [key: string]: { quantity: number; packetDescription?: string; code?: string; price?: number; category?: string } }) || {},
              livingston: updatedData.livingstonItems?.reduce((acc, item) => {
                acc[item.id] = {
                  quantity: item.quantity,
                  packetDescription: item.packetDescription,
                  code: item.code,
                  price: item.price,
                  category: item.category
                };
                return acc;
              }, {} as { [key: string]: { quantity: number; packetDescription?: string; code?: string; price?: number; category?: string } }) || {},
              flower: updatedData.flowerItems?.reduce((acc, item) => {
                acc[item.id] = {
                  quantity: item.quantity,
                  packetDescription: item.packetDescription,
                  code: item.code,
                  price: item.price,
                  category: item.category
                };
                return acc;
              }, {} as { [key: string]: { quantity: number; packetDescription?: string; code?: string; price?: number; category?: string } }) || {},
              vegetable: updatedData.vegetableItems?.reduce((acc, item) => {
                acc[item.id] = {
                  quantity: item.quantity,
                  packetDescription: item.packetDescription,
                  code: item.code,
                  price: item.price,
                  category: item.category
                };
                return acc;
              }, {} as { [key: string]: { quantity: number; packetDescription?: string; code?: string; price?: number; category?: string } }) || {},
            },
          });
        }, 0);
      }
      
      // If product items are updated, trigger auto-save to StoreOrders
      // This ensures product selections are saved immediately
      // Pass the updated data to ensure we save the latest state
      if (hasProductItems) {
        // Use a timeout to ensure state has updated, then save with the updated data
        setTimeout(() => {
          saveToStoreOrders(updatedData);
        }, 0);
      }
      
      return updatedData;
    });
  }, [saveOrderInfoToStoreOrders, saveToStoreOrders, cacheAutoSave]);

  const saveOrderData = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const dataToSave = {
        ...orderData,
        updatedAt: new Date(),
        status: 'draft',
        isDraft: true,
        draftSavedAt: new Date()
      };
      
      if (orderData.orderId) {
        // Update existing order
        const orderRef = doc(db, 'Stores', storeId, 'StoreOrders', orderData.orderId);
        await setDoc(orderRef, dataToSave, { merge: true });
      } else {
        // Create new order
        const ordersRef = collection(db, 'Stores', storeId, 'StoreOrders');
        const docRef = await addDoc(ordersRef, dataToSave);
        setOrderData(prev => ({
          ...prev,
          orderId: docRef.id
        }));
      }
      
      console.log('Order data saved successfully');
    } catch (err: any) {
      setError(err.message || 'Failed to save order data');
      console.error('Error saving order data:', err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [orderData, storeId]);

  const submitOrder = useCallback(async (): Promise<{ success: boolean; orderId?: string; pdfUrl?: string }> => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Validate required fields - make delivery date and order type optional for now
      // if (!orderData.deliveryDate || orderData.orderType === null) {
      //   throw new Error('Please fill in all required fields');
      // }
      
      // Check if there are any items selected
      const totalItems = [
        ...(orderData.heritageItems || []),
        ...(orderData.livingstonItems || []),
        ...(orderData.flowerItems || []),
        ...(orderData.vegetableItems || [])
      ].reduce((sum, item) => sum + item.quantity, 0);
      
      if (totalItems === 0) {
        throw new Error('Please select at least one item');
      }
      
      // Save final order data (still in draft mode, finalization happens separately)
      const finalOrderData = {
        ...orderData,
        status: 'draft',
        isDraft: true,
        submittedAt: new Date(),
        updatedAt: new Date(),
        draftSavedAt: new Date()
      };
      
      const orderRef = orderData.orderId 
        ? doc(db, 'Stores', storeId, 'StoreOrders', orderData.orderId)
        : collection(db, 'Stores', storeId, 'StoreOrders');
      
      let orderId: string;
      
      if (orderData.orderId) {
        await setDoc(orderRef as any, finalOrderData, { merge: true });
        orderId = orderData.orderId;
      } else {
        const docRef = await addDoc(orderRef as any, finalOrderData);
        orderId = docRef.id;
      }
      
      // Generate PDF URL (placeholder - you'll implement actual PDF generation)
      const pdfUrl = `/orders/${orderId}/pdf`;
      
      console.log('Order submitted successfully:', { orderId, pdfUrl });
      
      return { success: true, orderId, pdfUrl };
    } catch (err: any) {
      setError(err.message || 'Failed to submit order');
      console.error('Error submitting order:', err);
      return { success: false };
    } finally {
      setIsLoading(false);
    }
  }, [orderData, storeId]);

  const submitOrderWithEmail = useCallback(async (customerEmail: string): Promise<{ success: boolean; orderId?: string; pdfUrl?: string }> => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Validate required fields
      if (!customerEmail || !customerEmail.includes('@')) {
        throw new Error('Please enter a valid email address');
      }
      
      // Check for basic customer information
      if (!orderData.storeName || !orderData.address || !orderData.city) {
        throw new Error('Please fill in customer information (Store Name, Address, City)');
      }
      
      // Check if there are any items selected
      const totalItems = [
        ...(orderData.heritageItems || []),
        ...(orderData.livingstonItems || []),
        ...(orderData.flowerItems || []),
        ...(orderData.vegetableItems || [])
      ].reduce((sum, item) => sum + item.quantity, 0);
      
      if (totalItems === 0) {
        throw new Error('Please select at least one item');
      }
      
      // Generate order ID
      const orderId = orderData.orderId || `order_${Date.now()}`;
      
      // Generate PDF
      const pdfBlob = await generateOrderPDF(orderData);
      
      // Try to save to Firebase, but don't fail if permissions are insufficient
      let firebaseSuccess = false;
      try {
        const finalOrderData = {
          ...orderData,
          status: 'submitted',
          submittedAt: new Date(),
          updatedAt: new Date(),
          customerEmail: customerEmail,
          orderId: orderId
        };
        
        const orderRef = orderData.orderId 
          ? doc(db, 'Stores', storeId, 'StoreOrders', orderData.orderId)
          : collection(db, 'Stores', storeId, 'StoreOrders');
        
        if (orderData.orderId) {
          await setDoc(orderRef as any, finalOrderData, { merge: true });
        } else {
          await addDoc(orderRef as any, finalOrderData);
        }
        
        firebaseSuccess = true;
        console.log('Order saved to Firebase successfully');
      } catch (firebaseError: any) {
        console.warn('Firebase save failed (permissions issue):', firebaseError.message);
        // Continue with PDF generation and email sending even if Firebase fails
      }
      
      // Send emails (simulated for now)
      await sendOrderEmails(customerEmail, pdfBlob, orderId, orderData);
      
      // Generate PDF URL for redirect
      const pdfUrl = `/orders/${orderId}/pdf`;
      
      console.log('Order submitted successfully:', { orderId, pdfUrl, firebaseSuccess });
      
      return { success: true, orderId, pdfUrl };
    } catch (err: any) {
      setError(err.message || 'Failed to submit order');
      console.error('Error submitting order:', err);
      return { success: false };
    } finally {
      setIsLoading(false);
    }
  }, [orderData, storeId]);

  const resetOrderData = useCallback(async () => {
    try {
      // Delete ALL documents in the StoreOrders subcollection
      try {
        const ordersRef = collection(db, 'Stores', storeId, 'StoreOrders');
        const querySnapshot = await getDocs(ordersRef);
        
        if (!querySnapshot.empty) {
          const deletePromises = querySnapshot.docs.map(draftDoc => {
            return deleteDoc(doc(db, 'Stores', storeId, 'StoreOrders', draftDoc.id));
          });
          
          await Promise.all(deletePromises);
          console.log(`🗑️ Deleted ${querySnapshot.docs.length} document(s) from StoreOrders subcollection`);
        } else {
          console.log('📭 No documents found in StoreOrders subcollection to delete');
        }
      } catch (deleteErr) {
        console.error('⚠️ Failed to delete StoreOrders documents:', deleteErr);
        // Continue anyway - reset the frontend even if delete fails
      }
      
      // Clear cache as well
      try {
        await clearCache();
      } catch (cacheErr) {
        console.error('⚠️ Failed to clear cache:', cacheErr);
        // Continue anyway
      }
      
      // Reset all product selections and order data in the frontend
      setOrderData(getInitialOrderData());
      setError(null);
      
      console.log('🔄 Order data reset - all product selections cleared and StoreOrders subcollection deleted');
    } catch (err: any) {
      console.error('Error resetting order data:', err);
      // Still reset the frontend even if there's an error
      setOrderData(getInitialOrderData());
      setError(null);
    }
  }, [storeId, clearCache]);

  // Form-specific update functions
  const updateHeritageItems = useCallback((items: OrderItem[]) => {
    updateOrderData({ heritageItems: items });
  }, [updateOrderData]);

  const updateLivingstonItems = useCallback((items: OrderItem[]) => {
    updateOrderData({ livingstonItems: items });
  }, [updateOrderData]);

  const updateFlowerItems = useCallback((items: OrderItem[]) => {
    updateOrderData({ flowerItems: items });
  }, [updateOrderData]);

  const updateVegetableItems = useCallback((items: OrderItem[]) => {
    updateOrderData({ vegetableItems: items });
  }, [updateOrderData]);

  // Cache functions
  const saveDraft = useCallback(async () => {
    try {
      const cacheDataToSave: Partial<FormCacheData> = {
        orderData: {
          customerNumber: orderData.customerNumber,
          storeName: orderData.storeName,
          address: orderData.address,
          city: orderData.city,
          province: orderData.province,
          postalCode: orderData.postalCode,
          email: orderData.email,
          phoneNumber: orderData.phoneNumber,
          poNumber: orderData.poNumber,
          orderDate: orderData.orderDate,
          deliveryDate: orderData.deliveryDate,
          orderType: orderData.orderType,
          specialInstructions: orderData.specialInstructions,
        },
        formSelections: {
          heritage: orderData.heritageItems?.reduce((acc, item) => {
            acc[item.id] = {
              quantity: item.quantity,
              packetDescription: item.packetDescription,
              code: item.code,
              price: item.price,
              category: item.category
            };
            return acc;
          }, {} as { [key: string]: { quantity: number; packetDescription?: string; code?: string; price?: number; category?: string } }) || {},
          livingston: orderData.livingstonItems?.reduce((acc, item) => {
            acc[item.id] = {
              quantity: item.quantity,
              packetDescription: item.packetDescription,
              code: item.code,
              price: item.price,
              category: item.category
            };
            return acc;
          }, {} as { [key: string]: { quantity: number; packetDescription?: string; code?: string; price?: number; category?: string } }) || {},
          flower: orderData.flowerItems?.reduce((acc, item) => {
            acc[item.id] = {
              quantity: item.quantity,
              packetDescription: item.packetDescription,
              code: item.code,
              price: item.price,
              category: item.category
            };
            return acc;
          }, {} as { [key: string]: { quantity: number; packetDescription?: string; code?: string; price?: number; category?: string } }) || {},
          vegetable: orderData.vegetableItems?.reduce((acc, item) => {
            acc[item.id] = {
              quantity: item.quantity,
              packetDescription: item.packetDescription,
              code: item.code,
              price: item.price,
              category: item.category
            };
            return acc;
          }, {} as { [key: string]: { quantity: number; packetDescription?: string; code?: string; price?: number; category?: string } }) || {},
        },
        metadata: {
          userId: storeId,
          sessionId: `session_${Date.now()}`,
          formStatus: 'draft',
          lastUpdated: new Date(),
          totalItems: 
                     (orderData.heritageItems?.reduce((sum, item) => sum + item.quantity, 0) || 0) + 
                     (orderData.livingstonItems?.reduce((sum, item) => sum + item.quantity, 0) || 0) + 
                     (orderData.flowerItems?.reduce((sum, item) => sum + item.quantity, 0) || 0) + 
                     (orderData.vegetableItems?.reduce((sum, item) => sum + item.quantity, 0) || 0),
          totalAmount: (orderData.heritageItems?.reduce((sum, item) => sum + (item.price * item.quantity), 0) || 0) +
                      (orderData.livingstonItems?.reduce((sum, item) => sum + (item.price * item.quantity), 0) || 0) +
                      (orderData.flowerItems?.reduce((sum, item) => sum + (item.price * item.quantity), 0) || 0) +
                      (orderData.vegetableItems?.reduce((sum, item) => sum + (item.price * item.quantity), 0) || 0),
          version: 1,
        },
      };

      await saveCache(cacheDataToSave);
    } catch (error) {
      console.error('Failed to save draft:', error);
      throw error;
    }
  }, [orderData, storeId, saveCache]);

  const loadDraft = useCallback(async () => {
    try {
      await loadCache();
    } catch (error) {
      console.error('Failed to load draft:', error);
      throw error;
    }
  }, [loadCache]);

  const clearDraft = useCallback(async () => {
    try {
      // Delete the StoreOrders document if it exists
      if (orderData.orderId) {
        try {
          const orderRef = doc(db, 'Stores', storeId, 'StoreOrders', orderData.orderId);
          await deleteDoc(orderRef);
          console.log(`🗑️ Deleted StoreOrders document: ${orderData.orderId}`);
        } catch (deleteErr) {
          console.error('⚠️ Failed to delete StoreOrders document:', deleteErr);
          // Continue anyway - clear cache and frontend even if delete fails
        }
      }
      
      // Clear cache and reset order data
      await clearCache();
      setOrderData(getInitialOrderData());
      console.log('🧹 Draft cleared - all product selections and cache cleared');
    } catch (error) {
      console.error('Failed to clear draft:', error);
      // Still reset the frontend even if there's an error
      setOrderData(getInitialOrderData());
      throw error;
    }
  }, [orderData.orderId, storeId, clearCache]);

  const autoSave = useCallback(() => {
    // Use ref to get latest orderData without causing dependency issues
    const currentOrderData = orderDataRef.current;
    
    cacheAutoSave({
      orderData: {
        customerNumber: currentOrderData.customerNumber,
        storeName: currentOrderData.storeName,
        address: currentOrderData.address,
        city: currentOrderData.city,
        province: currentOrderData.province,
        postalCode: currentOrderData.postalCode,
        email: currentOrderData.email,
        phoneNumber: currentOrderData.phoneNumber,
        poNumber: currentOrderData.poNumber,
        orderDate: currentOrderData.orderDate,
        deliveryDate: currentOrderData.deliveryDate,
        orderType: currentOrderData.orderType,
        specialInstructions: currentOrderData.specialInstructions,
      },
      formSelections: {
        heritage: currentOrderData.heritageItems?.reduce((acc, item) => {
          acc[item.id] = {
            quantity: item.quantity,
            packetDescription: item.packetDescription,
            code: item.code,
            price: item.price,
            category: item.category
          };
          return acc;
        }, {} as { [key: string]: { quantity: number; packetDescription?: string; code?: string; price?: number; category?: string } }) || {},
        livingston: currentOrderData.livingstonItems?.reduce((acc, item) => {
          acc[item.id] = {
            quantity: item.quantity,
            packetDescription: item.packetDescription,
            code: item.code,
            price: item.price,
            category: item.category
          };
          return acc;
        }, {} as { [key: string]: { quantity: number; packetDescription?: string; code?: string; price?: number; category?: string } }) || {},
        flower: currentOrderData.flowerItems?.reduce((acc, item) => {
          acc[item.id] = {
            quantity: item.quantity,
            packetDescription: item.packetDescription,
            code: item.code,
            price: item.price,
            category: item.category
          };
          return acc;
        }, {} as { [key: string]: { quantity: number; packetDescription?: string; code?: string; price?: number; category?: string } }) || {},
        vegetable: currentOrderData.vegetableItems?.reduce((acc, item) => {
          acc[item.id] = {
            quantity: item.quantity,
            packetDescription: item.packetDescription,
            code: item.code,
            price: item.price,
            category: item.category
          };
          return acc;
        }, {} as { [key: string]: { quantity: number; packetDescription?: string; code?: string; price?: number; category?: string } }) || {},
      },
    });
    
    // Also save to StoreOrders in the background (will use ref internally)
    saveToStoreOrders();
  }, [cacheAutoSave, saveToStoreOrders]); // Removed orderData from dependencies to prevent infinite loops

  // Override getFormStatus to use orderData instead of cacheData
  // This ensures the status reflects items loaded from StoreOrders
  const getFormStatusFromOrderData = useCallback((formType: string): FormStatus => {
    let items: OrderItem[] = [];
    
    switch (formType) {
      case 'heritage':
        items = orderData.heritageItems || [];
        break;
      case 'livingston':
        items = orderData.livingstonItems || [];
        break;
      case 'flower':
        items = orderData.flowerItems || [];
        break;
      case 'vegetable':
        items = orderData.vegetableItems || [];
        break;
      default:
        items = [];
    }
    
    const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);
    const totalAmount = items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    
    return {
      status: itemCount > 0 ? 'in_progress' : 'empty',
      itemCount,
      totalAmount,
    };
  }, [orderData]);

  const value: GlobalOrderContextType = {
    orderData,
    isLoading,
    error,
    cacheData,
    isCacheLoading,
    isCacheSaving,
    lastSaved,
    hasUnsavedChanges,
    updateOrderData,
    saveOrderData,
    submitOrder,
    submitOrderWithEmail,
    resetOrderData,
    saveDraft,
    loadDraft,
    clearDraft,
    autoSave,
    updateHeritageItems,
    updateLivingstonItems,
    updateFlowerItems,
    updateVegetableItems,
    getFormStatus: getFormStatusFromOrderData,
    getTotalProgress
  };

  return (
    <GlobalOrderContext.Provider value={value}>
      {children}
    </GlobalOrderContext.Provider>
  );
};

export const useGlobalOrderData = (): GlobalOrderContextType => {
  const context = useContext(GlobalOrderContext);
  if (context === undefined) {
    throw new Error('useGlobalOrderData must be used within a GlobalOrderProvider');
  }
  return context;
};
